import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ModalController, IonicModule } from '@ionic/angular';
import { GroupInterface } from 'src/app/core/interfaces/group.interface';
import { NgIf } from '@angular/common';
import { FrontendConfig } from 'src/app/core/interfaces/frontend-config';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { CollapsableTitleInterface } from 'src/app/core/interfaces/Collapsable-title';
import { CollapsableTitleModalComponent } from 'src/app/shared/components/modals/collapsable-title-modal/collapsable-title-modal.component';

@Component({
  selector: 'app-group-modal',
  templateUrl: './group-modal.component.html',
  styleUrls: ['./group-modal.component.scss'],
  standalone: true,
  imports: [
    IonicModule,
    ReactiveFormsModule,
    NgIf,
    TranslateModule
  ],
})
export class GroupModalComponent implements OnInit {
  @Input()
  group?: GroupInterface;
  @Input()
  configuration?: FrontendConfig;
  @Input()
  collapsableTitles: CollapsableTitleInterface[] = [];
  groupForm!: FormGroup;
  groupFormProcessing = false;
  groupFormSubmitted = false;
  availableCollapsableTitles: CollapsableTitleInterface[] = [];
  selectedCollapsableTitles: string;
  selectedCollapsableTitleID: number = 0;

  get isCollapsable(): boolean {
    return !!this.groupForm?.get('Collapsable')?.value;
  }

  constructor(public modalController: ModalController,
              private translateService: TranslateService,
              private toastrService: ToastrService,
              private cdr: ChangeDetectorRef,
              private formBuilder: FormBuilder) {}

  ngOnInit() {
    this.groupForm = this.formBuilder.group({
      GroupID: [this.group ? this.group.GroupID : null],
      Active: [this.group ? this.group.Active : true],
      Name: [ this.group ? this.group.Name : null , [Validators.required]],
      Collapsable: [this.group ? !!this.group.Collapsable : false],
      CollapsableTitle: [
        this.group ? (this.group.CollapsableTitle ?? null) : null,
        this.group?.Collapsable ? [Validators.required] : null
      ],
      CollapsableTitleID: [this.group ? (this.group.CollapsableTitleID ?? null) : null],
    });
    this.initCollapsableTitles();
  }

  initCollapsableTitles(): void {
    this.availableCollapsableTitles = [...this.collapsableTitles];
    if (this.group?.Collapsable && this.group.CollapsableTitle) {
      if (!this.availableCollapsableTitles.some(t => t.Title === this.group!.CollapsableTitle)) {
        this.availableCollapsableTitles = [{ CollapsableTitleID: 0, Title: this.group.CollapsableTitle, Published: true, Description: '', SubTitles: [], EmbedConfig: [] }, ...this.availableCollapsableTitles];
      }
      this.selectedCollapsableTitles = this.group.CollapsableTitle;
    }
  }

  onCollapsableChange(): void {
    const titleControl = this.groupForm.get('CollapsableTitle');
    if (this.isCollapsable) {
      titleControl?.setValidators([Validators.required]);
    } else {
      titleControl?.clearValidators();
      titleControl?.setValue(null);
    }
    titleControl?.updateValueAndValidity();
  }

  handleChange(event: Event) {
    const target = event.target as HTMLIonSelectElement;
    const selectedTitle = target.value as string;
    const found = this.availableCollapsableTitles.find(t => t.Title === selectedTitle);
    this.selectedCollapsableTitles = selectedTitle;
    this.selectedCollapsableTitleID = found?.CollapsableTitleID ?? 0;
    this.groupForm.get('CollapsableTitleID')?.setValue(found?.CollapsableTitleID ?? null);
  }

  async addNewCollapsableTitle() {
    const modal = await this.modalController.create({
      component: CollapsableTitleModalComponent,
      cssClass: 'custom-modal',
      componentProps: { mode: 'create' }
    });
    await modal.present();

    const { data, role } = await modal.onDidDismiss();
    if (role === 'created' && data?.newTitleData) {
      const newEntry: CollapsableTitleInterface = data.newTitleData;
      this.availableCollapsableTitles = [...this.availableCollapsableTitles, newEntry];
      this.selectedCollapsableTitles = newEntry.Title;
      this.selectedCollapsableTitleID = newEntry.CollapsableTitleID ?? 0;
      this.groupForm.get('CollapsableTitle')?.setValue(newEntry.Title);
      this.groupForm.get('CollapsableTitleID')?.setValue(newEntry.CollapsableTitleID ?? null);
      this.cdr.detectChanges();
    }
  }

  saveGroup(): void {
    this.groupFormProcessing = true;
    this.groupFormSubmitted = true;
    if (this.groupForm.invalid) {
      this.groupFormProcessing = false;
      return;
    }

    const groupForm = this.groupForm.getRawValue();
    if (this.group) {
      groupForm.GroupID = this.group.GroupID;
    } else {
      delete groupForm.GroupID;
    }
    void this.modalController.dismiss({
      groupForm,
      action: this.group ? 'edit' : 'create'
    });
  }

  onBack() {
    this.modalController.dismiss(false);
  }
}
